import type { CopyAndImageProps as Props } from './types';
import { withChildren } from '@builder.io/react';
import { useColorProvider } from '@/utils/ColorProvider';
import { TextAlignmentOptions, HeroImagePlacements } from '@/constants';
import RichText from '@/components/RichText';
import { cls } from '@/utils/cls';
import style from './CopyAndImage.module.scss';
import Image from 'next/image';
export function CopyAndImage({
  extendStyles,
  heading,
  headingAlignment = TextAlignmentOptions.LEFT,
  copy,
  copyAlignment = TextAlignmentOptions.LEFT,
  image,
  color,
  line,
  children,
  textSide = HeroImagePlacements.LEFT,
  listColumns = false,
  mobileImage = true,
  headingSpan = false
}: Props) {
  const _className = cls(style.root, extendStyles);
  const {
    colors
  } = useColorProvider();
  const textLeft = textSide === TextAlignmentOptions.LEFT;
  const textStyles = cls(style.text, textLeft ? style.left : style.right);
  const imageStyles = cls(style.wrapper, textLeft ? style.right : style.left, headingSpan ? style.square : '');
  const copyStyles = cls(style.copy, listColumns ? style.cols : '');
  const wrapperStyles = cls(style.mobileWrapper, !mobileImage ? style.mobileHide : '', headingSpan ? style.square : '');
  const imageSrc = typeof image === 'string' ? image : image?.src;
  const squareSize = {
    width: 320,
    height: 320
  };
  const normalSize = {
    width: 600,
    height: 400
  };
  const imageSize = headingSpan ? squareSize : normalSize;

  // Match the actual rendered width per breakpoint so next/image fetches the right
  // resolution: full-width on mobile / half the 16-col grid on desktop (square caps at
  // 320px). The `0px` guard stops the off-breakpoint copy from also downloading.
  const mobileImageSizes = headingSpan ? '(max-width: 767px) 320px, 0px' : '(max-width: 767px) 100vw, 0px';
  const desktopImageSizes = headingSpan ? '(max-width: 767px) 0px, 320px' : '(max-width: 767px) 0px, 50vw';
  return <section className={_className} style={{
    backgroundColor: color || '#F9FAFB'
  }} data-sentry-component="CopyAndImage" data-sentry-source-file="CopyAndImage.tsx">
			<div className={style.content} data-grid>
				{heading && headingSpan && <RichText text={heading} className={cls(style.heading, !!line ? style.hr : '')} style={{
        borderColor: colors.secondaryAccentColor
      }} data-align={headingAlignment} />}
				<div className={textStyles}>
					{heading && !headingSpan && <RichText text={heading} className={!!line ? style.hr : ''} style={{
          borderColor: colors.secondaryAccentColor
        }} data-align={headingAlignment} />}
					{imageSrc && <div className={wrapperStyles}>
							<Image className={style.image} src={imageSrc} alt="" role="presentation" width={imageSize.width} height={imageSize.height} sizes={mobileImageSizes} />
						</div>}
					{copy && <RichText text={copy} className={copyStyles} style={{
          borderColor: colors.secondaryAccentColor
        }} data-align={copyAlignment} />}
					{children}
				</div>

				{imageSrc && <div className={imageStyles}>
						<Image className={style.image} src={imageSrc} alt="" role="presentation" width={imageSize.width} height={imageSize.height} sizes={desktopImageSizes} />
					</div>}
			</div>
		</section>;
}
export default withChildren(CopyAndImage);